import { FC, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import { Button, PopOver, useMessage } from '@pepper/ui'
import { disconnectGoogleAccount } from '@pepper/utils/src/api/integrations/google'

import GoogleIntegrationButton from 'components/GoogleIntegrationButton'
import DisconnectConfirmation from 'components/DisconnectConfirmation'
import { useSelectedCompanyId } from 'utils/functions/misc'
import analytics from 'assets/images/roi/analytics.png'
import { ReactComponent as GoogleLogo } from 'assets/images/google_logo.svg'

import s from './EmptyState.module.css'

type Props = {
  isGoogleAccountConnected: boolean
  connectedUser?: IIntegrationAccount
}

export const EmptyState: FC<Props> = ({
  isGoogleAccountConnected,
  connectedUser,
}) => {
  const companyId = useSelectedCompanyId()
  const userEmail = connectedUser?.url
  const accountId = connectedUser?.id
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  const { showError, showSuccess } = useMessage()

  const [showDisconnectConfirmation, setShowDisconnectConfirmation] =
    useState(false)

  const queryClient = useQueryClient()

  const handleToggle = () => {
    setShowDisconnectConfirmation(prevVal => !prevVal)
  }

  const handleDisconnect = async () => {
    setShowDisconnectConfirmation(false)
    setIsDisconnecting(true)
    const response = await disconnectGoogleAccount({
      accountId,
      companyId,
    })

    if (response.status === 'error') {
      showError(response.error)
      return
    }
    showSuccess('Account disconnected successfully.')

    queryClient.invalidateQueries(['profile-integrations', companyId])
    queryClient.invalidateQueries(['content-refresh-domains', companyId])
    setIsDisconnecting(false)
  }

  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <div className={s.iconContainer}>
          <div className={s.icon}>
            ✨
            <Icon
              icon="lucide:lock-keyhole"
              className={s.lockIcon}
              width={20}
            />
          </div>
        </div>
        <div>
          <div className={s.title}>
            {!isGoogleAccountConnected
              ? 'connect your google account'
              : 'try a different google account'}
          </div>
          <div className={s.header}>Unlock Content Analytics and ROI</div>
          <div className={s.subHeader}>
            {!isGoogleAccountConnected
              ? `Explore our platform's in-depth content analysis and Return on Investment insights to make data-driven decisions effortlessly.`
              : 'Your account is connected, but there are issues with your access properties. You can attempt to connect a different Google Account that provides access to both GA and GSC.'}
          </div>

          {!isGoogleAccountConnected ? (
            <GoogleIntegrationButton
              companyId={companyId}
              onError={error => showError(error)}
              cta="Connect Google Account"
              type="ghost"
            />
          ) : (
            <div className={s.disconnectWrapper}>
              <div className={s.user}>
                <GoogleLogo className={s.googleLogo} />
                {userEmail}
              </div>
              <PopOver
                visible={showDisconnectConfirmation}
                trigger="click"
                content={
                  <div className={s.disconnectPopover}>
                    <DisconnectConfirmation
                      onDisconnect={handleDisconnect}
                      onCancel={handleToggle}
                    />
                  </div>
                }
                placement="leftTop"
              >
                <Button
                  danger={true}
                  type="link"
                  size="middle"
                  onClick={handleToggle}
                  loading={isDisconnecting}
                  disabled={isDisconnecting}
                >
                  Try Another Account
                </Button>
              </PopOver>
            </div>
          )}
        </div>
        <div className={s.imageWrapper}>
          <img src={analytics} className={s.analytics} alt="analytics-roi" />
        </div>
      </div>
    </div>
  )
}
